import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { PortableTextContent } from "../components/news/PortableTextContent";
import { useNewsArticle } from "../lib/hooks/useNews";
import { buildArticleImageProps } from "../lib/sanity-image-urls";
import { format } from "date-fns";

export function NewsArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { data: article, isLoading, isError } = useNewsArticle(slug);

  const aspectRatio = article?.image?.asset?.metadata?.dimensions?.aspectRatio;
  const isPortrait = aspectRatio ? aspectRatio < 1 : false;

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12 text-gray-600">Loading article…</div>
      </div>
    );
  }

  if (isError || !article || !slug) {
    return (
      <div className="space-y-8">
        <Card>
          <CardContent className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Article Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The news article you're looking for doesn't exist.
            </p>
            <Link
              to="/news"
              className="inline-flex items-center text-primary-600 hover:text-primary-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to News
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const articleImageProps = article.image
    ? buildArticleImageProps(
        article.image,
        isPortrait ? "portrait" : "landscape",
      )
    : null;

  return (
    <div className="space-y-8">
      <Link
        to="/news"
        className="inline-flex items-center text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to News
      </Link>

      <Card>
        <CardHeader>
          <CardTitle className="text-3xl mb-2">{article.title}</CardTitle>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {format(new Date(article.date), "MMMM d, yyyy")}
            </span>
            {article.author?.name && (
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {article.author.name}
              </span>
            )}
          </div>
          {article.categories?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {article.categories.map((cat) => (
                <span
                  key={cat.slug?.current ?? cat.name}
                  className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-sm"
                >
                  {cat.name}
                </span>
              ))}
            </div>
          )}
        </CardHeader>
        <CardContent className="flow-root">
          {articleImageProps && (
            <div
              className={
                isPortrait
                  ? "float-left mr-6 mb-4 w-full md:w-1/3 rounded-lg overflow-hidden bg-gray-50"
                  : "mb-6 rounded-lg overflow-hidden flex justify-center bg-gray-50"
              }
            >
              <img
                src={articleImageProps.src}
                srcSet={articleImageProps.srcSet}
                sizes={articleImageProps.sizes}
                width={articleImageProps.width}
                height={articleImageProps.height}
                alt={article.title}
                className={
                  isPortrait
                    ? "w-full h-auto object-cover"
                    : "max-w-full max-h-[500px] object-contain"
                }
                decoding="async"
              />
            </div>
          )}
          <div className="prose prose-sm max-w-none">
            <PortableTextContent
              value={Array.isArray(article.content) ? article.content : []}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
